import { useCallback, useEffect, useState } from 'react';
import { api, formatDateTime, formatMoney, type WorkOrderDetail } from '../lib/api';
import { Button, Empty, ErrorBanner, Field, Panel, Spinner, StatusBadge } from '../lib/ui';
import { WORK_ORDER_STATUS_LABELS, type WorkOrderStatus } from '../../shared/work-order-status';
import { MEDIA_TYPE_LABELS, READ_DIRECTION_LABELS } from '../../shared/domain';

/** Everything about one job (brief §6). */
export function WorkOrderDetailPage({ id }: { id: string }) {
  const [data, setData] = useState<WorkOrderDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<WorkOrderDetail>(`/api/admin/work-orders/${id}`)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, [id]);

  useEffect(load, [load]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (error !== null && data === null) return <ErrorBanner error={error} />;
  if (data === null) return <Spinner />;

  const { workOrder: wo, unit, client, installer, items, photos, invoices, emails } = data;
  const approvedIds = new Set(photos.filter((p) => p.approvedForClient).map((p) => p.id));

  function toggleApproval(photoId: string): void {
    const next = new Set(approvedIds);
    if (next.has(photoId)) next.delete(photoId);
    else next.add(photoId);
    void act(() =>
      api.put(`/api/admin/work-orders/${id}/photos/approval`, { approvedPhotoIds: [...next] }),
    );
  }

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold tabular-nums">{wo.woNumber}</h1>
        <StatusBadge status={wo.status} />
        {wo.needsManualUnit && (
          <span className="border border-red-300 bg-red-50 px-1.5 py-0.5 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
            Unit not confirmed — cannot dispatch
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-1.5">
          {data.allowedTransitions.map((to) => (
            <Button
              key={to}
              variant={to === 'approved' || to === 'dispatched' ? 'primary' : to === 'cancelled' || to === 'rejected' ? 'danger' : 'default'}
              disabled={busy}
              onClick={() => {
                const reason =
                  to === 'cancelled' || to === 'rejected'
                    ? (window.prompt(`Reason for ${WORK_ORDER_STATUS_LABELS[to].toLowerCase()}?`) ?? '')
                    : undefined;
                void act(() =>
                  api.post(`/api/admin/work-orders/${id}/transition`, { to, reason }),
                );
              }}
            >
              {transitionLabel(to)}
            </Button>
          ))}
          <Button disabled={busy} onClick={() => void act(() => api.post(`/api/admin/work-orders/${id}/pdf`))}>
            Rebuild PDF
          </Button>
          {wo.pdfR2Key !== null && (
            <a
              href={`/r2/work-order/${id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="border border-neutral-300 bg-white px-2.5 py-1 text-xs hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800"
            >
              Open PDF
            </a>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Job">
          <dl>
            <Field label="Client">{client?.name}</Field>
            <Field label="Campaign">{wo.campaignName}</Field>
            <Field label="Installer">
              {installer ? `${installer.companyName}${installer.primaryContactName ? ` · ${installer.primaryContactName}` : ''}` : null}
            </Field>
            <Field label="Install date">{wo.requestedInstallDateText ?? wo.requestedInstallDate}</Field>
            <Field label="Deadline">{wo.deadlineOn}</Field>
            <Field label="Copy">{wo.copyDescription}</Field>
            <Field label="Instructions">{wo.specialInstructions}</Field>
            <Field label="Material">
              {[wo.materialCarrier, wo.materialTrackingNumber].filter(Boolean).join(' · ') || null}
            </Field>
            <Field label="Installer notes">{wo.installerNotes}</Field>
          </dl>
        </Panel>

        <Panel title="Unit">
          {unit === null ? (
            <Empty>No unit assigned. A wrong unit sends a crew to the wrong city — confirm before dispatch.</Empty>
          ) : (
            <dl>
              <Field label="Code">{unit.unitCode}</Field>
              <Field label="Geopath / TAB">{unit.geopathId}</Field>
              <Field label="Description">{unit.description}</Field>
              <Field label="Market">{unit.dma}</Field>
              <Field label="Media type">{MEDIA_TYPE_LABELS[unit.mediaType]}</Field>
              <Field label="Read">{unit.readDirection ? READ_DIRECTION_LABELS[unit.readDirection] : null}</Field>
              <Field label="Size">{unit.sizeText}</Field>
              <Field label="Map">
                {unit.latitude !== null && unit.longitude !== null ? (
                  <a
                    className="text-blue-700 underline dark:text-blue-400"
                    href={`https://www.google.com/maps/search/?api=1&query=${unit.latitude},${unit.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {unit.latitude.toFixed(5)}, {unit.longitude.toFixed(5)}
                  </a>
                ) : null}
              </Field>
            </dl>
          )}
        </Panel>

        <Panel title={`Checklist (${items.filter((i) => i.isCompleted).length}/${items.length})`}>
          {items.length === 0 ? (
            <Empty>No checklist items.</Empty>
          ) : (
            <ul className="space-y-1">
              {items.map((item) => (
                <li key={item.id} className="flex gap-2 text-xs">
                  <span className={item.isCompleted ? 'text-green-600' : 'text-neutral-400'}>
                    {item.isCompleted ? '✓' : '○'}
                  </span>
                  <span className={item.isCompleted ? 'text-neutral-500 line-through' : ''}>
                    {item.label}
                    {item.isRequired && <span className="ml-1 text-red-500" title="Required">*</span>}
                  </span>
                  {item.installerNote !== null && (
                    <span className="ml-auto text-neutral-500 italic">{item.installerNote}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={`Photos (${photos.length})`}>
          {photos.length === 0 ? (
            <Empty>No photos uploaded yet.</Empty>
          ) : (
            <>
              <p className="mb-2 text-xs text-neutral-500">
                Tick a photo to release it to the client. Only ticked photos are ever sent.
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {photos.map((p) => (
                  <label key={p.id} className="block cursor-pointer">
                    <img
                      src={`/r2/photo/${p.id}`}
                      alt={p.category}
                      loading="lazy"
                      className={`aspect-square w-full border-2 object-cover ${
                        approvedIds.has(p.id) ? 'border-green-500' : 'border-transparent'
                      }`}
                    />
                    <span className="mt-0.5 flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={approvedIds.has(p.id)}
                        onChange={() => toggleApproval(p.id)}
                        disabled={busy}
                      />
                      <span className="uppercase text-neutral-500">{p.category}</span>
                      {p.exifLatitude !== null && (
                        <span title="Geotagged — corroborates the crew was on site">📍</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </Panel>

        <Panel title="Invoice">
          {invoices.length === 0 ? (
            <Empty>No invoice submitted.</Empty>
          ) : (
            <dl>
              {invoices.map((inv) => (
                <Field key={inv.id} label={inv.invoiceNumber ?? 'Invoice'}>
                  {formatMoney(inv.amountCents)} · {inv.approvalStatus}
                </Field>
              ))}
            </dl>
          )}
        </Panel>

        <Panel title={`Email log (${emails.length})`}>
          {emails.length === 0 ? (
            <Empty>No email sent or received for this job.</Empty>
          ) : (
            <ul className="space-y-1 text-xs">
              {emails.map((e) => (
                <EmailRow key={e.id} email={e} />
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function transitionLabel(to: WorkOrderStatus): string {
  switch (to) {
    case 'pending_review':
      return 'Send to review';
    case 'dispatched':
      return 'Dispatch';
    case 'approved':
      return 'Approve & send proof';
    case 'rejected':
      return 'Reject';
    case 'cancelled':
      return 'Cancel';
    case 'closed':
      return 'Close';
    default:
      return WORK_ORDER_STATUS_LABELS[to];
  }
}

/**
 * One row of the email log, expandable to the message that was actually sent.
 *
 * The log recorded who, when and which template, but the body lives in R2, so
 * it answered "we emailed them" and never "what did we tell them" — which is
 * the question that comes up when an installer says the gate code was wrong.
 *
 * Rendered in a sandboxed iframe: this is HTML written by our templates but
 * carrying customer-supplied strings, and it has no business running script or
 * navigating the app.
 */
function EmailRow({
  email,
}: {
  email: {
    id: string;
    direction: string;
    subject: string | null;
    status: string;
    createdAt: number;
    toAddresses: string[];
  };
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState<{ html: string; text: string } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function toggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (!next || body !== null) return;
    try {
      const d = await api.get<{ html: string; text: string }>(
        `/api/admin/work-orders/emails/${email.id}/body`,
      );
      setBody(d);
    } catch (err: unknown) {
      setProblem(err instanceof Error ? err.message : 'Could not load the message.');
    }
  }

  return (
    <li className="border-b border-neutral-100 pb-1 dark:border-neutral-900">
      <button onClick={() => void toggle()} className="flex w-full gap-2 text-left">
        <span className="text-neutral-400">{email.direction === 'inbound' ? '↓' : '↑'}</span>
        <span className="min-w-0 flex-1 truncate">{email.subject ?? '(no subject)'}</span>
        <span className="text-neutral-500">{email.status}</span>
        <span className="whitespace-nowrap text-neutral-400">{formatDateTime(email.createdAt)}</span>
        <span className="text-neutral-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="mt-1 border border-neutral-200 p-2 dark:border-neutral-800">
          <p className="mb-1 text-neutral-500">To: {email.toAddresses.join(', ')}</p>
          {problem !== null ? (
            <p className="text-neutral-500">{problem}</p>
          ) : body === null ? (
            <p className="text-neutral-500">Loading…</p>
          ) : (
            <iframe
              title={`Message ${email.id}`}
              sandbox=""
              srcDoc={body.html}
              className="h-96 w-full border border-neutral-200 bg-white dark:border-neutral-800"
            />
          )}
        </div>
      )}
    </li>
  );
}
